/**
 * Content Management Tree View Component
 * Simple folder-based document browser with minimal iconography
 */

import React from 'react';
import {
  Tree,
  Card,
  Empty,
  Checkbox,
  Space,
  Button,
} from 'antd';
import {
  FolderOutlined,
  FolderOpenOutlined,
  DeleteOutlined,
  PlusOutlined,
  MinusOutlined,
} from '@ant-design/icons';

const ContentManagementTreeView = ({
  documents,
  selectedDocuments,
  onDocumentSelect,
  onDocumentClick,
  onFolderDelete,
}) => {
  // Build folder structure from documents
  const buildFolderTree = () => {
    // Group documents by folder_path
    const folderMap = {};

    documents.forEach((doc) => {
      const folderPath = doc.folder_path || '/';
      if (!folderMap[folderPath]) {
        folderMap[folderPath] = [];
      }
      folderMap[folderPath].push(doc);
    });

    // Build tree nodes
    const buildNodes = (path) => {
      const nodes = [];
      const currentDocs = folderMap[path] || [];
      const subfolders = new Set();

      // Find all subfolders
      Object.keys(folderMap).forEach((folderPath) => {
        if (folderPath.startsWith(path) && folderPath !== path) {
          const relativePath = path === '/' ? folderPath.substring(1) : folderPath.substring(path.length + 1);
          const firstSegment = relativePath.split('/')[0];
          if (firstSegment) {
            subfolders.add(firstSegment);
          }
        }
      });

      // Add subfolder nodes
      Array.from(subfolders).sort().forEach((folderName) => {
        const subfolderPath = path === '/' ? `/${folderName}` : `${path}/${folderName}`;
        nodes.push({
          title: (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
              }}
            >
              <Space size="small">
                <FolderOutlined style={{ color: 'var(--rlc-state-warning)' }} />
                <span>{folderName}</span>
              </Space>
              {onFolderDelete && (
                <Button
                  size="small"
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    onFolderDelete(subfolderPath);
                  }}
                  style={{ marginLeft: 8 }}
                />
              )}
            </div>
          ),
          key: `folder-${subfolderPath}`,
          icon: null,
          children: buildNodes(subfolderPath),
          selectable: false,
          isLeaf: false,
        });
      });

      // Add document nodes
      currentDocs.forEach((doc) => {
        const isSelected = selectedDocuments.includes(doc.id);
        const uploadDate = doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString() : 'N/A';

        nodes.push({
          title: (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                cursor: 'pointer',
              }}
              onClick={(e) => {
                // Don't trigger if clicking checkbox
                if (e.target.type !== 'checkbox') {
                  onDocumentClick(doc);
                }
              }}
            >
              <Space size="small" style={{ flex: 1 }}>
                <Checkbox
                  checked={isSelected}
                  onChange={(e) => {
                    e.stopPropagation();
                    onDocumentSelect(doc.id, e.target.checked);
                  }}
                  onClick={(e) => e.stopPropagation()}
                />
                <span style={{ color: 'var(--rlc-text-muted)', fontSize: '12px', minWidth: '80px' }}>
                  {uploadDate}
                </span>
                <span>{doc.title}</span>
              </Space>
            </div>
          ),
          key: `doc-${doc.id}`,
          icon: null,
          isLeaf: true,
          data: doc,
        });
      });

      return nodes;
    };

    return buildNodes('/');
  };

  const treeData = buildFolderTree();

  if (documents.length === 0) {
    return (
      <Card>
        <Empty description="No documents found" />
      </Card>
    );
  }

  return (
    <Card
      style={{ marginTop: '0', borderRadius: '8px' }}
      bodyStyle={{ padding: '16px', maxHeight: 'calc(100vh - 280px)', overflow: 'auto' }}
    >
      <Tree
        showIcon
        defaultExpandAll
        switcherIcon={({ expanded }) =>
          expanded ? <MinusOutlined /> : <PlusOutlined />
        }
        treeData={treeData}
        selectable={false}
      />
    </Card>
  );
};

export default ContentManagementTreeView;
